import {
  Stack,
  Typography,
  TextField,
  Chip,
  Autocomplete,
  createFilterOptions,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { RHFTextField } from 'src/components/hook-form';
import { Controller, useForm } from 'react-hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { createNewInterview } from 'src/services/interview.service';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import { SourceInput } from 'src/components/source-input';
import Iconify from 'src/components/iconify';

const filter = createFilterOptions();

export default function AddManually({
  jobId,
  inputSourceFormatOptions,
  setInputSourceFormatOptions,
  isFetchingTags,
  tags,
  allTags,
  handleAddNewTag,
  fetchTags,
  setTags,
  setAllTags,
  getSortedTags
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const defaultValues = useMemo(
    () => ({
      fullName: '',
      email: '',
      phone: '',
      source: '',
      tags: [],
    }),
    [],
  );

  const schema = Yup.object().shape({
    fullName: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string(),
    source: Yup.string(),
    tags: Yup.array(),
  });

  // states
  const [tagInputValue, setTagInputValue] = useState('');

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // functions

  const onSubmit = handleSubmit(async data => {
    try {
      const reqBody = {
        name: data.fullName,
        email: data.email,
        phone_no: data.phone,
        candidate_source: data.source,
      };

      if (data.tags.length === 0 && tagInputValue !== '') {
        reqBody.tags = [tagInputValue];
      } else {
        reqBody.tags = data.tags;
      }

      const interviewResponse = await createNewInterview(reqBody, jobId);

      if (interviewResponse.success) {
        enqueueSnackbar('Candidate Added');
        navigate(paths.dashboard.jobs.details(jobId));
      } else {
        throw new Error(interviewResponse.message);
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar({
        message: 'Error occured while adding candidate',
        variant: 'error',
      });
    } finally {
      reset();
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing="20px">
        <Stack spacing="6px">
          <Typography variant="paragraph" fontWeight={600}>
            Full Name
          </Typography>
          <RHFTextField
            name="fullName"
            type="text"
            placeholder="Enter full name"
          />
        </Stack>
        <Stack spacing="6px">
          <Typography variant="paragraph" fontWeight={600}>
            Email
          </Typography>
          <RHFTextField name="email" type="email" placeholder="Enter email" />
        </Stack>
        <Stack spacing="6px">
          <Typography variant="paragraph" fontWeight={600}>
            Phone
          </Typography>
          <RHFTextField name="phone" placeholder="Enter phone number" />
        </Stack>
        <Stack spacing="6px">
          <Typography variant="paragraph" fontWeight={600}>
            Source
          </Typography>
          <SourceInput
            inputSourceFormatOptions={inputSourceFormatOptions}
            setInputSourceFormatOptions={setInputSourceFormatOptions}
            methods={methods}
          />
        </Stack>
        <Stack spacing="6px">
          <Typography variant="paragraph" fontWeight={600}>
            Tags
          </Typography>
          <Controller
            name="tags"
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                multiple
                onOpen={()=> getSortedTags(methods.getValues().tags, tags)}
                loading={isFetchingTags}
                options={
                  tags.length
                    ? tags
                        .map(tag => ({
                          label: tag,
                          value: tag,
                        }))
                    : []
                }
                getOptionLabel={option => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return option.label;
                }}
                renderOption={(props, option, state, ownerState)=>{
                  const currentTags = methods.getValues().tags;

                  const isTagFound = currentTags ? currentTags.includes(option.label) : false;

                  return (
                    <MenuItem
                    component="li"
                    {...props}
                    selected={isTagFound}

                  >
                       <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="caption">{option.label}</Typography>
                    {isTagFound && <Iconify icon="tabler:check" width={20} />}
                  </Stack>
                    </MenuItem>
                  )

                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;

                  const isExisting = options.some(
                    option =>
                      inputValue.toLowerCase() === option.label.toLowerCase(),
                  );

                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                inputValue={tagInputValue}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue.endsWith(',')) {
                    let oldValues = methods.getValues();
                    oldValues = oldValues.tags;

                    let newValueArr = newInputValue.split(',').slice(0, -1);
                    newValueArr = oldValues.concat(newValueArr);

                    methods.setValue('tags', newValueArr, {
                      shouldValidate: true,
                    });

                    setTagInputValue('');
                  } else {
                    setTagInputValue(newInputValue);
                  }
                }}
                onChange={async (event, newValue) => {
                  const _tags = newValue
                    .map(val => {
                      if(typeof val === 'string'){
                        return val;
                      }
                      if(val?.value){
                        return val.value;
                      }
                      return null
                    })
                    .filter(val => val !== null);

                  if (newValue.some(option => option.inputValue)) {
                    const newTag = newValue.find(option => option.inputValue);

                    const newTagValue = newTag.inputValue
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    await handleAddNewTag(newTagValue);
                    await fetchTags();

                    _tags.unshift(newTagValue);

                    methods.setValue('tags', _tags, {
                      shouldValidate: true,
                    });
                  }
                  else{
                    methods.setValue('tags', _tags, {
                      shouldValidate: true,
                    });
                  }
                }}
                isOptionEqualToValue={(option, value) =>  option.value === value}
                renderInput={params => (
                  <TextField
                    placeholder="Add tag and press Enter"
                    error={!!error}
                    {...params}
                    helperText={error ? error?.message : ''}
                  />
                )}
                freeSolo
                disableCloseOnSelect
                handleHomeEndKeys
                autoHighlight
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      color="info"
                      variant="soft"
                    />
                  ))
                }
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack mt="24px">
        <LoadingButton
          fullWidth
          variant="contained"
          loading={isSubmitting}
          type="submit"
        >
          Add candidate
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}

AddManually.propTypes = {
  inputSourceFormatOptions: PropTypes.array.isRequired,
  jobId: PropTypes.string.isRequired,
  setInputSourceFormatOptions: PropTypes.func.isRequired,
  isFetchingTags: PropTypes.bool.isRequired,
  tags: PropTypes.array.isRequired,
  handleAddNewTag: PropTypes.func.isRequired,
  fetchTags: PropTypes.func.isRequired,
  setTags: PropTypes.func.isRequired,
  setAllTags: PropTypes.func.isRequired,
  allTags: PropTypes.array.isRequired,
  getSortedTags: PropTypes.func.isRequired
};
